import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PaginationState } from "@/components/Datatable/components/DataTablePagination/types";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { listDiagnosticCompanies } from "../api/listDiagnosticCompanies";
import { isCancellation } from "../utils/rejections";
import type { LoadStatus } from "../utils/loadStatus";
import type { DiagnosticCompanyRow } from "../types/diagnostic";

const SEARCH_DEBOUNCE_MS = 300;

/**
 * One of the sizes `DataTablePagination` offers — [10, 20, 30, 40, 50] (D19).
 *
 * Not the endpoint's own default of 25: a size the items-per-page control has
 * no option for leaves that control blank until it is touched.
 */
const DIRECTORY_PAGE_SIZE = 20;

/**
 * The diagnosed-companies directory.
 *
 * Paging and search are both server-side: this list is one row per tenant that
 * has ever been diagnosed, and the endpoint used to return every attempt in
 * the system so the page could be filtered in the browser. Only the page in
 * view is fetched now, so the search box has to reach the server too.
 */
export function useDiagnosticCompanies() {
  /**
   * The page as the server last served it, or `null` until one has arrived.
   *
   * A page object rather than a bare array, precisely so that "the server said
   * there are no companies" and "no server has ever answered" are different
   * values. Held together with its count because they are one answer.
   */
  const [page, setPage] = useState<{
    companies: DiagnosticCompanyRow[];
    totalCount: number;
  } | null>(null);
  const [status, setStatus] = useState<LoadStatus>("loading");
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: DIRECTORY_PAGE_SIZE,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebouncedValue(searchTerm, SEARCH_DEBOUNCE_MS);
  const abortControllerRef = useRef<AbortController | null>(null);

  const cancelPendingRequest = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  const startRequest = useCallback(() => {
    cancelPendingRequest();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    return controller;
  }, [cancelPendingRequest]);

  const requestData = useMemo(
    () => ({
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      q: debouncedSearchTerm.trim() || undefined,
    }),
    [pagination, debouncedSearchTerm],
  );

  const refetch = useCallback(() => {
    const controller = startRequest();
    setStatus("loading");
    listDiagnosticCompanies(requestData, controller.signal)
      .then((result) => {
        setPage({
          companies: result.companies,
          totalCount: result.totalCount,
        });
        setStatus("loaded");
      })
      .catch((error) => {
        // A cancelled request does not own the status. Typing in the search
        // box aborts the previous fetch, and that abort settles *after* the
        // replacement has set the flag — so settling it unconditionally
        // dropped the spinner while the newer search was still in flight, and
        // would now report the superseded request's cancellation as a failure.
        //
        // Everything else has already reached the user through the global
        // interceptor. Recording it is what lets the screen say the list could
        // not be read instead of drawing the empty state over it (D10).
        if (!isCancellation(error)) setStatus("failed");
      });
  }, [startRequest, requestData]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => cancelPendingRequest, [cancelPendingRequest]);

  const handlePageChange = useCallback(
    (pageIndex: number, pageSize: number) => {
      setPagination({ pageIndex, pageSize });
    },
    [],
  );

  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
    // A filtered list is a different list; page 4 of the old one is
    // meaningless. Returning `prev` unchanged when already on page 1 lets
    // React bail out — allocating a fresh object per keystroke changed the
    // `requestData` memo, which changed `refetch`, which re-ran the fetch
    // effect, so the request went out per character and the debounce bought
    // nothing at all. Same bailout as `useCompanyDiagnostics`'s company reset.
    setPagination((prev) =>
      prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 },
    );
  }, []);

  return {
    companies: page?.companies ?? [],
    totalCount: page?.totalCount ?? 0,
    /** False until a page has ever arrived — see `LoadStatus`. */
    hasLoaded: page !== null,
    status,
    loading: status === "loading",
    pagination,
    searchTerm,
    handlePageChange,
    handleSearchChange,
    refetch,
  };
}
